import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "@/lib/icons";
import { useEffect, useRef, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { toast } from "sonner";
import { formatDateOnly, statusLabel, TOUR_STATUSES, errorWords, PAGE_SIZES } from "@alm/domain";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";

import { DataTable } from "@/components/app/patterns/data-table";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { TableCard } from "@/components/app/patterns/table-card";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { optionalOneOf, searchPatch, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { TourRowActions } from "./-components/tour-actions";
import { awaitingOutcome, tourColumns } from "./-components/tour-columns";
import { TourDialog, type TourRow } from "./-components/tour-dialog";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/_crm/crm/tours/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    edit?: string;
    schedule?: string;
    q?: string;
    status?: TourStatus;
    page?: number;
    size?: number;
  } => ({
    edit: typeof search.edit === "string" ? search.edit : undefined,
    schedule: typeof search.schedule === "string" ? search.schedule : undefined,
    q: optionalSearch(search.q),
    status: optionalOneOf(TOUR_STATUSES, search.status),
    page: Number(search.page) > 1 ? Math.floor(Number(search.page)) : undefined,
    size: PAGE_SIZES.find((size) => size === Number(search.size)),
  }),
  component: Tours,
});

type Editing = { tour: TourRow | null; leadId?: string } | null;
type TourStatus = (typeof TOUR_STATUSES)[number];

function Tours() {
  const { facilityId } = useFacility();
  const canWrite = useCan("crm:manage");
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const search = Route.useSearch();
  const [editing, setEditing] = useState<Editing>(null);
  const [removing, setRemoving] = useState<TourRow | null>(null);
  const { q: needle, status: statusFilter, page = 1, size: pageSize = 50 } = search;
  const status = statusFilter ?? "all";
  const refine = (next: Partial<typeof search>) => patch({ page: undefined, ...next });
  // Page 3 of one building means nothing in the next: a switch starts again at the first page.
  const shownFacility = useRef(facilityId);
  useEffect(() => {
    if (shownFacility.current === facilityId) return;
    shownFacility.current = facilityId;
    void navigate({ search: (prev) => ({ ...prev, page: undefined }), ...KEEP_PLACE });
  }, [facilityId, navigate]);
  const {
    text: query,
    change: setQuery,
    settling,
  } = useSearchParamText(needle, (q) => refine({ q }));
  const [today] = useState(() => new Date().toLocaleDateString("en-CA"));

  // Every tour, latest scheduled day first, so upcoming tours lead instead of
  // the building's oldest.
  const tours = useQuery({
    ...orpc.crm.tours.list.queryOptions({
      input: {
        facilityId,
        status: statusFilter,
        search: needle || undefined,
        dir: "desc",
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || tours.isPlaceholderData;
  const stillScheduled = useQuery({
    ...orpc.crm.tours.list.queryOptions({
      input: { facilityId, status: "scheduled", search: needle || undefined, limit: 1 },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const rows = tours.data?.rows ?? [];
  const total = tours.data?.total ?? 0;

  // "/crm/tours?edit=<id>" and "?schedule=<leadId>" arrive from a lead's Tours tab.
  const loaded = Boolean(tours.data);
  useEffect(() => {
    if (!search.schedule && !(search.edit && loaded)) return;
    const editId = search.edit;
    const linked = editId ? rows.find((tour) => tour.id === editId) : undefined;
    // oxlint-disable-next-line react/set-state-in-effect -- a deep link opens its dialog once
    if (linked) setEditing({ tour: linked });
    else if (search.schedule) setEditing({ tour: null, leadId: search.schedule });
    // A tour outside the loaded page, or in another building, is read on its own.
    else if (editId) {
      void orpc.crm.tours.get
        .call({ tourId: editId })
        .then((tour) =>
          setEditing({
            tour: {
              ...tour,
              contacts: tour.contactIds.map((id) => ({
                id,
                firstName: "",
                lastName: "",
                relationship: "",
                email: null,
              })),
            },
          }),
        )
        .catch(() => toast.error("That tour could not be found."));
    }
    void navigate({
      search: (prev) => ({ ...prev, edit: undefined, schedule: undefined }),
      replace: true,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs when the link or the list arrives
  }, [search.edit, search.schedule, loaded]);

  const remove = useMutation(
    orpc.crm.tours.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Tour deleted");
        await queryClient.invalidateQueries({ queryKey: orpc.crm.tours.key() });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const filtered = Boolean(needle) || status !== "all";
  const showGuide = rows.some((tour) => tour.tourGuideName);
  const scheduled = stillScheduled.data?.total ?? 0;
  const clear = () => {
    setQuery("");
    refine({ q: undefined, status: undefined });
  };
  const pagination: PaginationState = { pageIndex: page - 1, pageSize };
  const schedule = () => setEditing({ tour: null });
  const columns = tourColumns({ today, showGuide });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Tours"
        count={tours.isPending || tours.isError ? undefined : total}
        meta={
          tours.isPending || tours.isError || stillScheduled.isPending
            ? undefined
            : `${scheduled} still scheduled`
        }
        actions={
          canWrite ? (
            <Button onClick={schedule}>
              <Plus className="size-4" />
              Schedule tour
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          placeholder="Search by lead name"
          label="Search tours by lead name"
          value={query}
          onChange={setQuery}
        />
        <OptionSelect
          value={status}
          aria-label="Filter by status"
          onChange={(event) => refine({ status: optionalOneOf(TOUR_STATUSES, event.target.value) })}
        >
          <SelectOption value="all">All statuses</SelectOption>
          {TOUR_STATUSES.map((option) => (
            <SelectOption key={option} value={option}>
              {statusLabel(option)}
            </SelectOption>
          ))}
        </OptionSelect>
        {filtered ? (
          <Button variant="ghost" onClick={clear}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {tours.isError ? (
        <TableCard>
          <ReadFailed
            className="p-4"
            message="Couldn't load tours. None are shown because none were read."
            error={tours.error}
            retrying={tours.isFetching}
            onRetry={() => void tours.refetch()}
          />
        </TableCard>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          noun="tours"
          rowId={(tour) => tour.id}
          busy={searching}
          pending={tours.isPending || (searching && rows.length === 0)}
          server={{
            rowCount: total,
            pagination,
            onPaginationChange: (next) =>
              patch({
                page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined,
                size: next.pageSize === 50 ? undefined : next.pageSize,
              }),
          }}
          onRowClick={canWrite ? (tour) => setEditing({ tour }) : undefined}
          renderRowActions={(tour) => {
            const name = `${tour.leadFirstName} ${tour.leadLastName}`;
            return (
              <TourRowActions
                tour={tour}
                label={`${name}'s tour`}
                canWrite={canWrite}
                onEdit={() => setEditing({ tour })}
                onRemove={() => setRemoving(tour)}
                primary={
                  awaitingOutcome(tour, today) &&
                  canWrite && { label: "Record outcome", onSelect: () => setEditing({ tour }) }
                }
              />
            );
          }}
          empty={
            <EmptyState
              title={filtered ? "No tours match these filters" : "No tours scheduled"}
              description={
                filtered
                  ? "Widen the search or clear a filter to see more."
                  : "Schedule a tour and it appears here with its reminder and calendar actions."
              }
              action={
                filtered ? (
                  <Button variant="outline" onClick={clear}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      {editing ? (
        <TourDialog
          facilityId={facilityId}
          tour={editing.tour}
          initialLeadId={editing.leadId}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {removing ? (
        <RemoveConfirm
          title={`Delete ${removing.leadFirstName} ${removing.leadLastName}'s tour?`}
          description={`The tour on ${formatDateOnly(removing.scheduledDate)} is removed for good. To keep a record that it was called off, set it to Cancelled instead.`}
          confirmLabel="Delete tour"
          onConfirm={() => remove.mutate({ tourId: removing.id })}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
